import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFlag, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api';
import { DeployTestItem, DeployTestItemStatus, DeployTestStatus } from '../types';
import { resolveItemStatus } from './views/deployTestUtils';

export interface LockedActiveTest {
  _id: string;
  name: string;
  track: 'internal' | 'beta';
  status: DeployTestStatus;
  items: DeployTestItem[];
}

interface ActiveTestReleaseLockModalProps {
  show: boolean;
  message: string;
  tests: LockedActiveTest[];
  continuing?: boolean;
  onClose: () => void;
  onContinue: () => void;
  onTestsChange?: (tests: LockedActiveTest[]) => void;
}

const testsApiFor = (track: LockedActiveTest['track']) =>
  track === 'internal' ? '/internal-tests' : '/deploy-tests';

const ActiveTestReleaseLockModal: React.FC<ActiveTestReleaseLockModalProps> = ({
  show,
  message,
  tests,
  continuing = false,
  onClose,
  onContinue,
  onTestsChange,
}) => {
  const [localTests, setLocalTests] = useState<LockedActiveTest[]>(tests);
  const [error, setError] = useState<string | null>(null);
  const [savingTestId, setSavingTestId] = useState<string | null>(null);

  useEffect(() => {
    setLocalTests(tests);
    setError(null);
  }, [tests]);

  const allTestsResolved = useMemo(
    () => localTests.length > 0 && localTests.every((test) => test.status === 'succeeded' || test.status === 'failed'),
    [localTests]
  );

  const updateLocalTest = (testId: string, updater: (test: LockedActiveTest) => LockedActiveTest) => {
    setLocalTests((prev) => {
      const next = prev.map((test) => (test._id === testId ? updater(test) : test));
      onTestsChange?.(next);
      return next;
    });
  };

  const handleItemStatusChange = async (
    test: LockedActiveTest,
    itemIndex: number,
    status: DeployTestItemStatus
  ) => {
    if (test.status !== 'testing') return;

    const items = (test.items || []).map((item, index) => ({
      _id: item._id,
      text: item.text,
      description: item.description || '',
      status: index === itemIndex ? status : resolveItemStatus(item),
    }));

    updateLocalTest(test._id, (current) => ({
      ...current,
      items: current.items.map((item, index) => (
        index === itemIndex ? { ...item, status } : item
      )),
    }));

    setSavingTestId(test._id);
    setError(null);
    try {
      await axios.patch(
        `${API_BASE_URL}${testsApiFor(test.track)}/${test._id}`,
        { items },
        { withCredentials: true }
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update task status');
    } finally {
      setSavingTestId(null);
    }
  };

  const handleTestStatusChange = async (test: LockedActiveTest, status: 'succeeded' | 'failed') => {
    if (test.status !== 'testing') return;
    const unresolved = (test.items || []).some((item) => resolveItemStatus(item) === 'testing');
    if (unresolved) {
      setError('Pass or fail each task before passing or failing the test.');
      return;
    }

    setSavingTestId(test._id);
    setError(null);
    try {
      await axios.patch(
        `${API_BASE_URL}${testsApiFor(test.track)}/${test._id}`,
        { status },
        { withCredentials: true }
      );
      updateLocalTest(test._id, (current) => ({ ...current, status }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update test status');
    } finally {
      setSavingTestId(null);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show active-test-lock-modal"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="active-test-lock-title"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="active-test-lock-title">
                Finish the open test first
              </h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                {message}
              </div>
              <p className="text-muted mb-3">
                Pass or fail each task, then pass or fail the test. After that you can continue disabling or changing this release.
              </p>

              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}

              {localTests.map((test) => {
                const items = test.items || [];
                const unresolvedTasks = items.some((item) => resolveItemStatus(item) === 'testing');
                const testDone = test.status === 'succeeded' || test.status === 'failed';
                const busy = savingTestId === test._id;

                return (
                  <div key={test._id} className="active-test-lock-card">
                    <div className="active-test-lock-card-head">
                      <div>
                        <div className="fw-bold">{test.name}</div>
                        <div className="text-muted small text-capitalize">{test.track} test</div>
                      </div>
                      <span className={`active-test-lock-pill is-${test.status}`}>
                        {test.status === 'succeeded' ? 'Passed' : test.status === 'failed' ? 'Failed' : 'Testing'}
                      </span>
                    </div>

                    {items.length ? (
                      <ul className="active-test-lock-tasks">
                        {items.map((item, index) => {
                          const itemStatus = resolveItemStatus(item);
                          return (
                            <li key={item._id || `${test._id}-${index}`} className={`is-${itemStatus}`}>
                              <span className="active-test-lock-task-title">{item.text}</span>
                              <div className="active-test-lock-task-actions">
                                <button
                                  type="button"
                                  className={`btn btn-sm ${itemStatus === 'passed' ? 'btn-success' : 'btn-outline-success'}`}
                                  disabled={testDone || busy}
                                  onClick={() => handleItemStatusChange(test, index, 'passed')}
                                >
                                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                                  Pass
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${itemStatus === 'flagged' ? 'btn-flagged' : 'btn-outline-flagged'}`}
                                  disabled={testDone || busy}
                                  onClick={() => handleItemStatusChange(test, index, 'flagged')}
                                >
                                  <FontAwesomeIcon icon={faFlag} className="me-1" />
                                  Flagged
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${itemStatus === 'failed' ? 'btn-danger' : 'btn-outline-danger'}`}
                                  disabled={testDone || busy}
                                  onClick={() => handleItemStatusChange(test, index, 'failed')}
                                >
                                  <FontAwesomeIcon icon={faXmark} className="me-1" />
                                  Fail
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="text-muted small mb-2">No tasks on this test.</div>
                    )}

                    {!testDone && (
                      <div className="active-test-lock-test-actions">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          disabled={busy || unresolvedTasks}
                          title={unresolvedTasks ? 'Pass or fail each task first' : 'Pass this test'}
                          onClick={() => handleTestStatusChange(test, 'succeeded')}
                        >
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Pass test
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={busy || unresolvedTasks}
                          title={unresolvedTasks ? 'Pass or fail each task first' : 'Fail this test'}
                          onClick={() => handleTestStatusChange(test, 'failed')}
                        >
                          <FontAwesomeIcon icon={faXmark} className="me-1" />
                          Fail test
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={continuing}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onContinue}
                disabled={!allTestsResolved || continuing}
                title={allTestsResolved ? 'Continue with the release change' : 'Pass or fail the open test first'}
              >
                {continuing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Continuing...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show active-test-lock-backdrop" />
    </>
  );
};

export default ActiveTestReleaseLockModal;
